import React from 'react';
import SEO from '../components/SEO';
import './RefundPolicy.css';

const RefundPolicy = () => {
  return (
    <div className="refund-policy-container">
      <SEO 
        title="Refund Policy - BlackKiteAI"
        description="BlackKiteAI refund and money-back policy. Learn about our credit refund terms and conditions."
        canonical="https://www.blackkiteai.com/refund-policy"
      />
      <div className="refund-policy-content">
        <h1>Refund & Money-Back Policy</h1>
        <p className="last-updated">Last Updated: December 23, 2025</p>

        <section>
          <p>
            At BlackKiteAI, we are committed to providing a transparent and fair refund policy. 
            Please read this policy carefully before purchasing credits on our platform.
          </p>
        </section>

        <section>
          <h2>1. Overview</h2>
          <p>
            BlackKiteAI operates on a prepaid, usage-based credit system. Credits are purchased 
            in advance and consumed as you use our AI-powered services. This policy outlines the 
            conditions under which refunds may be requested and processed.
          </p>
        </section>

        <section>
          <h2>2. Refund Eligibility</h2>
          <p>
            Refunds are available <strong>only</strong> under the following strict conditions:
          </p>
          <ul>
            <li>
              <strong>Zero Usage Requirement:</strong> The purchased credits must remain 
              completely unused. Even a partial consumption of 0.5 credits or more disqualifies 
              the purchase from being refundable.
            </li>
            <li>
              <strong>30-Day Inactivity Rule:</strong> Credits must remain unused for 30 consecutive 
              days from the date of purchase before becoming eligible for a refund. After the 30-day 
              period, you may request a refund at any time.
            </li>
            <li>
              <strong>Valid Purchase:</strong> The payment transaction must have been successfully 
              completed and the credits must have been added to your account.
            </li>
          </ul>
          <p className="highlight">
            <strong>Important:</strong> If you have used any portion of your purchased credits, 
            your purchase is considered final and non-refundable, regardless of the amount remaining.
          </p>
        </section>

        <section>
          <h2>3. Non-Refundable Scenarios</h2>
          <p>
            The following situations are <strong>not eligible</strong> for refunds under any circumstances:
          </p>
          <ul>
            <li>Credits that have been partially or fully consumed</li>
            <li>Credits transferred or shared with other accounts</li>
            <li>Credits purchased during promotional offers or discount campaigns (unless explicitly stated otherwise)</li>
            <li>Service dissatisfaction after credit usage has begun</li>
            <li>Changes in personal circumstances or business requirements</li>
            <li>Failure to use credits due to lack of time or changed priorities</li>
            <li>Technical issues on the user's end (internet connectivity, hardware, etc.)</li>
            <li>Violation of our Terms of Service resulting in account suspension or termination</li>
          </ul>
        </section>

        <section>
          <h2>4. Failed Payment Transactions</h2>
          <p>
            In the event of a failed payment transaction where money has been deducted from your 
            account but credits were not added to your BlackKiteAI account:
          </p>
          <ul>
            <li>
              Our payment gateway provider automatically processes refunds for failed 
              transactions according to their standard procedures.
            </li>
            <li>
              Refunds for failed transactions are typically processed within 5-7 business days, 
              though this may vary depending on your bank or payment method.
            </li>
            <li>
              You do not need to submit a refund request for failed transactions. The refund 
              will be initiated automatically.
            </li>
            <li>
              If you do not receive your refund within 10 business days, please contact our 
              support team with your transaction details.
            </li>
          </ul>
        </section>

        <section>
          <h2>5. Refund Method & Timeline</h2>
          <p>
            <strong>Refund Method:</strong> All approved refunds will be processed to the original 
            payment method used for the purchase. We do not offer refunds via alternative payment 
            methods or credit transfers to other accounts.
          </p>
          <p>
            <strong>Processing Timeline:</strong>
          </p>
          <ul>
            <li>Refund requests are reviewed within 5-7 business days of submission</li>
            <li>Once approved, refunds are processed within 3-5 business days</li>
            <li>The refund amount will appear in your account within 7-10 business days from approval, 
                depending on your bank or card issuer</li>
          </ul>
          <p className="note">
            <strong>Note:</strong> BlackKiteAI is not responsible for delays caused by banks, 
            financial institutions, or payment processors.
          </p>
        </section>

        <section>
          <h2>6. How to Request a Refund</h2>
          <p>
            If you meet the eligibility criteria outlined in Section 2, you may request a refund 
            by following these steps:
          </p>contacting
          <ul>
            <li>Your registered email address</li>
            <li>Transaction ID or order number</li>
            <li>Purchase date</li>
            <li>Reason for refund request</li>
          </ul>
          <p className="note">
            Please ensure all information provided is accurate. Incomplete or incorrect information 
            may delay the processing of your refund request.
          </p>
        </section>

        <section>
          <h2>7. Refund Amount</h2>
          <p>
            Approved refunds will be for the full purchase amount paid, excluding any transaction 
            fees, processing charges, or currency conversion fees that may have been applied at 
            the time of purchase. These fees are determined by third-party payment processors 
            and are non-refundable.
          </p>
        </section>

        <section>
          <h2>8. Account Status After Refund</h2>
          <p>
            Once a refund is processed:
          </p>
          <ul>
            <li>The refunded credits will be deducted from your account balance</li>
            <li>Your account will remain active and you may continue using any remaining credits</li>
            <li>You may make new purchases at any time</li>
            <li>Repeated refund requests may be subject to additional review</li>
          </ul>
        </section>

        <section>
          <h2>9. Chargebacks & Disputes</h2>
          <p>
            We strongly encourage you to contact us directly before initiating a chargeback 
            with your bank or credit card company. Initiating a chargeback without first 
            contacting us may result in:
          </p>
          <ul>
            <li>Immediate suspension of your account</li>
            <li>Permanent termination of access to BlackKiteAI services</li>
            <li>Legal action to recover costs associated with the chargeback process</li>
          </ul>
          <p>
            We are committed to resolving all disputes fairly and promptly through our official 
            refund request process.
          </p>
        </section>

        <section>
          <h2>10. Changes to This Policy</h2>
          <p>
            BlackKiteAI reserves the right to modify or update this Refund & Money-Back Policy 
            at any time. Changes will be effective immediately upon posting on our website. 
            The "Last Updated" date at the top of this policy indicates when it was last revised.
          </p>
          <p>
            Your continued use of BlackKiteAI services after any changes constitutes acceptance 
            of the revised policy. We recommend reviewing this policy periodically.
          </p>
        </section>

        <section>
          <h2>11. Contact Information</h2>
          <p>
            If you have any questions about this Refund & Money-Back Policy or need assistance 
            with a refund request, please contact us:
          </p>
          <div className="contact-info">
            <p><strong>Email:</strong> support@blackkiteai.com</p>
            <p><strong>Support Hours:</strong> Monday - Friday, 9:00 AM - 6:00 PM (IST)</p>
          </div>
        </section>

        <div className="policy-footer">
          <p>
            By purchasing credits on BlackKiteAI, you acknowledge that you have read, understood, 
            and agree to be bound by this Refund & Money-Back Policy.
          </p>
        </div>
      </div>
    </div>
  );
};

export default RefundPolicy;
